// LagbasN → Time Flow: bygger JSON som hjälpskriptet "Flatbox → Time Flow" läser.
// Formatet heter tikab.timeflow.v1. Ändras fältnamnen måste hjälpskriptet ändras samtidigt.

export const TIMEFLOW_SCHEMA = 'tikab.timeflow.v1';

const digits = (s) => String(s ?? '').replace(/\D/g, '');

/**
 * @param {object} input
 * @param {object} input.kund
 *   namn*, orgnr* (10 siffror, med eller utan bindestreck), momsnr, adress (gata/box),
 *   adress2 (t.ex. FE-nummer/att-rad), postnr, ort, land ('SE' standard),
 *   fakturaEpost, kundensReferens (= "Er referens", hur kunden vill att fakturan märks;
 *   blir kundkontakt på projektets kundrad i Time Flow), betalvillkorDagar (30 standard)
 * @param {object} input.projekt
 *   namn*, nummer (projektnummer i Time Flow = offertnumret, t.ex. FB2728; utelämnas det
 *   föreslås nästa lediga nummer), ertOrdernr, fakturanotering, beskrivning,
 *   resultatenhet ('MVL'), projekttyp ('01' = BTIV | VR/Visualisering), projektledareId ('11')
 * @param {object} [input.faktura]
 *   nummer, datum, belopp, valuta, artikel ('1602'), prisPerEnhet (läggs som Pris/enhet
 *   på artikeln i projektets prislista = pris per fakturaperiod), ordinariePrisPerEnhet och
 *   kampanjSlut (vid prova-på/kampanj), text, period, samt för artikelregistreringens notering:
 *   produkt ('Flatbox'), periodFran / periodTill (ÅÅÅÅ-MM-DD för den fakturerade perioden),
 *   anvandare (antal, t.ex. 25), kampanj (true/false)
 * @returns {object} payload
 */
export function buildTimeFlowPayload({ kund = {}, projekt = {}, faktura = {} }) {
  const fel = [];
  if (!kund.namn) fel.push('Kundnamn saknas');
  const org = digits(kund.orgnr);
  if (!(org.length === 10 || org.length === 12)) fel.push('Org.nr saknas eller är ogiltigt');
  if (!projekt.namn) fel.push('Projektnamn saknas');
  if (fel.length) throw new Error(fel.join(', '));

  const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));

  return {
    schema: TIMEFLOW_SCHEMA,
    skapad: new Date().toISOString(),
    kund: clean({
      namn: kund.namn.trim(),
      orgnr: kund.orgnr,
      momsnr: kund.momsnr,
      adress: kund.adress,
      adress2: kund.adress2,
      postnr: kund.postnr,
      ort: kund.ort,
      land: kund.land || 'SE',
      fakturaEpost: kund.fakturaEpost,
      kundensReferens: kund.kundensReferens,
      betalvillkorDagar: kund.betalvillkorDagar,
    }),
    projekt: clean({
      namn: projekt.namn.trim(),
      nummer: projekt.nummer,
      ertOrdernr: projekt.ertOrdernr,
      fakturanotering: projekt.fakturanotering,
      beskrivning: projekt.beskrivning,
      resultatenhet: projekt.resultatenhet,
      projekttyp: projekt.projekttyp,
      projektledareId: projekt.projektledareId,
    }),
    faktura: clean({
      nummer: faktura.nummer,
      datum: faktura.datum,
      belopp: faktura.belopp,
      valuta: faktura.valuta || 'SEK',
      artikel: faktura.artikel || '1602',
      prisPerEnhet: faktura.prisPerEnhet,
      ordinariePrisPerEnhet: faktura.ordinariePrisPerEnhet,
      kampanjSlut: faktura.kampanjSlut,
      // För noteringen på artikelregistreringen: "Flatbox 3 mån (261104-270203) 25 användare [KAMPANJ]"
      produkt: faktura.produkt,
      periodFran: faktura.periodFran,
      periodTill: faktura.periodTill,
      anvandare: faktura.anvandare,
      kampanj: faktura.kampanj,
      text: faktura.text,
      period: faktura.period,
    }),
  };
}

/** Bygger payload och lägger den i urklipp. Returnerar payloaden. */
export async function copyTimeFlowPayload(input) {
  const payload = buildTimeFlowPayload(input);
  await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  return payload;
}
